/**
 * The ⌘K palette.
 *
 * It knows nothing about accounts, markets, transfers or routes: it is handed
 * groups of items and it calls `onSelect`. Everything domain-shaped — what
 * matched, how it ranked, where it goes — belongs to the caller, so this
 * component can be lifted with the rest of the kit (ADR-0005).
 *
 * The one non-obvious decision, and the thing hand-rolled palettes almost
 * always get wrong: DOM focus never leaves the input. Rows are `role="option"`
 * and the cursor is virtual, carried by `aria-activedescendant`. Moving real
 * focus onto a row would swallow the next keystroke, so the operator could not
 * arrow down and keep typing — which is the whole interaction.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { CommandDialog } from './overlay'
import { Icon, type IconName } from './Icon'
import { StateBlock } from './core'

export interface CommandPaletteItem {
  id: string
  /** May contain <mark> fragments; supply `label` when it does. */
  title: ReactNode
  subtitle?: ReactNode
  /** Right-aligned trailing content — a money badge, a role chip, a timestamp. */
  meta?: ReactNode
  icon?: IconName
  onSelect: () => void
  /**
   * Accessible name. Required in practice whenever `title` is highlighted: a
   * <mark> splits the text node, and a screen reader then reads the row in
   * fragments.
   */
  label?: string
}

export interface CommandPaletteGroup {
  id: string
  label: ReactNode
  items: CommandPaletteItem[]
  /** Right-aligned note in the group head — "Showing 5 of 41". */
  note?: ReactNode
}

export interface CommandPaletteProps {
  open: boolean
  onClose: () => void
  query: string
  onQueryChange: (next: string) => void
  groups: CommandPaletteGroup[]
  loading?: boolean
  /** Rendered in place of the groups when every group is empty. */
  empty?: ReactNode
  /** The keyboard legend. */
  footer?: ReactNode
  label?: string
  placeholder?: string
  /** sr-only live announcement, e.g. "41 results — 5 agents, 3 in the live book". */
  status?: ReactNode
  /**
   * ⌘⏎ / Ctrl⏎ — "open without leaving here". Operators do compare two members.
   * Falls back to `onSelect` when not supplied.
   */
  onSelectAlternate?: (item: CommandPaletteItem, group: CommandPaletteGroup) => void
  /** Closes on select. Off if the caller wants to keep the palette open. */
  closeOnSelect?: boolean
}

interface FlatRow {
  item: CommandPaletteItem
  group: CommandPaletteGroup
  /** Index of the first row of this row's group — Tab's landing place. */
  groupStart: number
}

export function CommandPalette({
  open,
  onClose,
  query,
  onQueryChange,
  groups,
  loading,
  empty,
  footer,
  label = 'Search',
  placeholder = 'Search',
  status,
  onSelectAlternate,
  closeOnSelect = true,
}: CommandPaletteProps) {
  const baseId = useId()
  const listId = `${baseId}-list`
  const listRef = useRef<HTMLDivElement>(null)

  const { flat, indexOf } = useMemo(() => {
    const out: FlatRow[] = []
    const map = new Map<CommandPaletteItem, number>()
    for (const group of groups) {
      const groupStart = out.length
      for (const item of group.items) {
        map.set(item, out.length)
        out.push({ item, group, groupStart })
      }
    }
    return { flat: out, indexOf: map }
  }, [groups])

  const [cursor, setCursor] = useState(0)

  // Reset the cursor when the RESULT SET changes identity, not on every render.
  // Resetting per render would throw away a cursor the operator moved as soon as
  // a background refetch settled; never resetting would leave it pointing at a
  // row that no longer exists.
  const identity = flat.map((row) => row.item.id).join(' ')
  const lastIdentity = useRef(identity)
  useEffect(() => {
    if (lastIdentity.current === identity) return
    lastIdentity.current = identity
    setCursor(0)
  }, [identity])

  useEffect(() => {
    if (open) setCursor(0)
  }, [open])

  const rowId = useCallback((index: number) => `${baseId}-opt-${index}`, [baseId])

  // `nearest`, not `center` — centring makes the list lurch under the cursor on
  // every arrow press.
  useEffect(() => {
    if (!open) return
    const el = listRef.current?.ownerDocument.getElementById(rowId(cursor))
    el?.scrollIntoView({ block: 'nearest' })
  }, [cursor, open, rowId, identity])

  const choose = (index: number, alternate: boolean) => {
    const row = flat[index]
    if (!row) return
    if (alternate && onSelectAlternate) onSelectAlternate(row.item, row.group)
    else row.item.onSelect()
    if (closeOnSelect) onClose()
  }

  const move = (delta: number) => {
    if (flat.length === 0) return
    setCursor((current) => (current + delta + flat.length) % flat.length)
  }

  const jumpGroup = (direction: 1 | -1) => {
    if (flat.length === 0) return
    setCursor((current) => {
      const here = flat[current]
      if (!here) return 0
      if (direction === 1) {
        const next = groups.findIndex((g) => g.id === here.group.id) + 1
        const target = flat.findIndex((row) => row.group.id === groups[next]?.id)
        return target >= 0 ? target : 0
      }
      // Shift+Tab from anywhere but the first row of a group goes to that row
      // first, which is how every other grouped list behaves.
      if (current > here.groupStart) return here.groupStart
      const previous = groups.findIndex((g) => g.id === here.group.id) - 1
      const target = flat.findIndex((row) => row.group.id === groups[previous]?.id)
      return target >= 0 ? target : flat.length - 1
    })
  }

  const onInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        move(1)
        return
      case 'ArrowUp':
        event.preventDefault()
        move(-1)
        return
      case 'Home':
        event.preventDefault()
        setCursor(0)
        return
      case 'End':
        event.preventDefault()
        setCursor(Math.max(0, flat.length - 1))
        return
      case 'Tab':
        // Tab moves between groups and never leaves the palette. There is
        // nowhere else in the dialog worth reaching by Tab, and letting it
        // escape to the page behind the scrim is a dead end.
        event.preventDefault()
        jumpGroup(event.shiftKey ? -1 : 1)
        return
      case 'Enter':
        event.preventDefault()
        choose(cursor, event.metaKey || event.ctrlKey)
        return
      case 'Escape':
        // Two presses always exit, from anywhere: the first clears what you
        // typed, the second closes. Closing on the first would throw away a
        // long query on a mistaken keystroke.
        if (query.length > 0) {
          event.preventDefault()
          event.stopPropagation()
          onQueryChange('')
        }
        return
      default:
    }
  }

  const hasRows = flat.length > 0

  return (
    <CommandDialog
      open={open}
      onClose={onClose}
      label={label}
      value={query}
      onValueChange={onQueryChange}
      placeholder={placeholder}
      busy={loading}
      activeId={hasRows ? rowId(cursor) : undefined}
      listId={listId}
      footer={footer}
      status={status}
      onInputKeyDown={onInputKeyDown}
    >
      <div ref={listRef} id={listId} role="listbox" aria-label={label}>
        {hasRows
          ? groups.map((group) => {
              if (group.items.length === 0) return null
              const headId = `${baseId}-grp-${group.id}`
              return (
                <div key={group.id} className="w7-cmdk-group" role="group" aria-labelledby={headId}>
                  <div className="w7-cmdk-grouphead" id={headId}>
                    <span>{group.label}</span>
                    {group.note ? <span className="w7-cmdk-groupnote">{group.note}</span> : null}
                  </div>
                  {group.items.map((item) => {
                    const index = indexOf.get(item) ?? -1
                    const active = index === cursor
                    return (
                      // A div, not a button. An option that is also a button is
                      // announced with two roles and two names.
                      <div
                        key={item.id}
                        id={rowId(index)}
                        role="option"
                        aria-selected={active}
                        aria-label={item.label}
                        className="w7-cmdk-row"
                        data-active={active ? 'true' : undefined}
                        // `mousemove`, not `mouseenter`: enter fires when the
                        // list scrolls under a stationary pointer and steals the
                        // cursor from the keyboard.
                        onMouseMove={() => setCursor(index)}
                        onClick={(event) => choose(index, event.metaKey || event.ctrlKey)}
                      >
                        {item.icon ? <Icon name={item.icon} size={16} /> : <span className="w7-cmdk-noicon" />}
                        <span className="w7-cmdk-text">
                          <span className="w7-cmdk-title w7-truncate">{item.title}</span>
                          {item.subtitle ? (
                            <span className="w7-cmdk-sub w7-truncate">{item.subtitle}</span>
                          ) : null}
                        </span>
                        {item.meta ? <span className="w7-cmdk-meta">{item.meta}</span> : null}
                      </div>
                    )
                  })}
                </div>
              )
            })
          : (empty ?? <StateBlock icon="search" title="Nothing matches" />)}
      </div>
    </CommandDialog>
  )
}
